import React, { useState } from "react";
import { AcceptJsToken, PaymentSource } from "../../types";
import {
  InlinePaymentForm,
  InlinePaymentFields,
  isInlineFormComplete,
} from "../InlinePaymentForm/InlinePaymentForm";
import { tokenizeCard } from "../../utils/tokenizeCard";

type Props = {
  // The save-card action. The parent calls CARA's POST /payment-source on
  // its end and returns the resulting saved card.
  onAddCard: (token: AcceptJsToken) => Promise<PaymentSource>;
  // Called after a successful save so the shell can prepend the new card
  // to its picker and auto-select it.
  onCardAdded: (saved: PaymentSource) => void;
};

const empty: InlinePaymentFields = {
  cardholderName: "",
  cardNumber: "",
  expMonth: "",
  expYear: "",
  cvc: "",
};

export function AddCardPanel({ onAddCard, onCardAdded }: Props) {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState<InlinePaymentFields>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFields(empty);
    setError(null);
    setOpen(false);
  };

  const save = async () => {
    if (!isInlineFormComplete(fields)) return;
    setBusy(true);
    setError(null);
    try {
      const token = await tokenizeCard(fields);
      const saved = await onAddCard(token);
      onCardAdded(saved);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save card");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        className="rounded-md border border-gray-300 dark:border-slate-700 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-800"
        data-testid="add-card-button"
        onClick={() => setOpen(true)}
        type="button"
      >
        Add a card
      </button>
    );
  }

  return (
    <section
      className="rounded-lg border border-gray-200 dark:border-slate-700 p-4 space-y-3"
      data-testid="add-card-panel"
    >
      <h3 className="text-base font-medium text-gray-900 dark:text-white">
        Add a card
      </h3>
      <InlinePaymentForm onChange={setFields} value={fields} />
      {error ? (
        <p className="text-sm text-red-600" data-testid="add-card-error">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          className="rounded-md bg-primary text-white px-3 py-2 text-sm disabled:opacity-50"
          data-testid="save-card-button"
          disabled={busy || !isInlineFormComplete(fields)}
          onClick={save}
          type="button"
        >
          {busy ? "Saving…" : "Save card"}
        </button>
        <button
          className="rounded-md border border-gray-300 dark:border-slate-700 px-3 py-2 text-sm disabled:opacity-50"
          disabled={busy}
          onClick={reset}
          type="button"
        >
          Cancel
        </button>
      </div>
    </section>
  );
}
